import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ShoppingCart,
  AccountCircle,
  MenuBook,
  Login,
  Logout,
  Person,
  ShoppingBag,
  AdminPanelSettings,
  LocalShipping,
  Language,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { RootState } from '../../types';
import { AppDispatch } from '../../store/store';
import SearchBar from '../search/SearchBar';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { totalItems } = useSelector((state: RootState) => state.cart);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    handleLangMenuClose();
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
    setDrawerOpen(false);
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
    setDrawerOpen(false);
  };

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  return (
    <>
    <AppBar position="static" sx={{ backgroundColor: '#d4af37' }}>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
        {/* Logo - Компактен на mobile */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="logo"
          onClick={() => navigate('/')}
          sx={{ mr: { xs: 0.5, sm: 2 } }}
        >
          <MenuBook />
        </IconButton>

        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 'bold',
            mr: { xs: 1, sm: 3 },
            fontSize: { xs: '1rem', sm: '1.25rem' },
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Golden Books
        </Typography>

        {/* Mobile: показва само "GB" */}
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 'bold',
            mr: 1,
            fontSize: '1rem',
            display: { xs: 'block', sm: 'none' },
          }}
        >
          GB
        </Typography>

        {/* Search Bar - по-малък на mobile */}
        <Box sx={{
          flexGrow: 1,
          maxWidth: { xs: 'none', md: 600 },
          mx: { xs: 0.5, sm: 2 }
        }}>
          <SearchBar
            placeholder={t('common.searchPlaceholder')}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
          {/* Desktop: всички бутони */}
          {!isMobile && (
            <>
              <Button
                color="inherit"
                onClick={handleLangMenuOpen}
                startIcon={<Language />}
                sx={{ minWidth: 'auto' }}
              >
                {i18n.language === 'bg' ? 'БГ' : 'EN'}
              </Button>
              <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
              >
                <MenuItem
                  onClick={() => handleLanguageChange('bg')}
                  selected={i18n.language === 'bg'}
                >
                  🇧🇬 Български
                </MenuItem>
                <MenuItem
                  onClick={() => handleLanguageChange('en')}
                  selected={i18n.language === 'en'}
                >
                  🇬🇧 English
                </MenuItem>
              </Menu>

              <Button
                color="inherit"
                onClick={() => navigate('/shop')}
                startIcon={<ShoppingBag />}
              >
                {t('common.shop')}
              </Button>

              <Button
                color="inherit"
                onClick={() => navigate('/order-tracking')}
                startIcon={<LocalShipping />}
              >
                {t('common.trackOrder')}
              </Button>
            </>
          )}

          {/* Cart - винаги видим */}
          <IconButton
            color="inherit"
            onClick={() => navigate('/cart')}
            aria-label="cart"
          >
            <Badge badgeContent={totalItems} color="error">
              <ShoppingCart />
            </Badge>
          </IconButton>

          {/* Desktop: User menu */}
          {!isMobile && (
            <>
              {isAuthenticated ? (
                <>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      {user?.firstName?.charAt(0) || <AccountCircle />}
                    </Avatar>
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem disabled sx={{ opacity: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {user?.email}
                      </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => handleNavigate('/profile')}>
                      <Person sx={{ mr: 1 }} fontSize="small" />
                      {t('common.myProfile')}
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigate('/orders')}>
                      <ShoppingBag sx={{ mr: 1 }} fontSize="small" />
                      {t('common.myOrders')}
                    </MenuItem>
                    {user?.role === 'ADMIN' && (
                      <MenuItem onClick={() => handleNavigate('/admin')}>
                        <AdminPanelSettings sx={{ mr: 1 }} fontSize="small" />
                        {t('common.adminPanel')}
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <Logout sx={{ mr: 1 }} fontSize="small" />
                      {t('common.logout')}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  color="inherit"
                  onClick={() => navigate('/login')}
                  startIcon={<Login />}
                >
                  {t('common.login')}
                </Button>
              )}
            </>
          )}

          {/* Mobile: Hamburger menu */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={() => toggleDrawer(true)}
              edge="end"
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>

    {/* Mobile Drawer Menu */}
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => toggleDrawer(false)}
    >
      <Box
        sx={{ width: 280, pt: 2 }}
        role="presentation"
      >
        <List>
          {/* User Info */}
          {isAuthenticated && user && (
            <>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    {user?.firstName?.charAt(0) || <AccountCircle />}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={user.firstName || user.email}
                  secondary={user.email}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                />
              </ListItem>
              <Divider />
            </>
          )}

          {/* Language */}
          <ListItem button onClick={handleLangMenuOpen}>
            <ListItemIcon>
              <Language />
            </ListItemIcon>
            <ListItemText primary={i18n.language === 'bg' ? '🇧🇬 Български' : '🇬🇧 English'} />
          </ListItem>
          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={handleLangMenuClose}
          >
            <MenuItem
              onClick={() => handleLanguageChange('bg')}
              selected={i18n.language === 'bg'}
            >
              🇧🇬 Български
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange('en')}
              selected={i18n.language === 'en'}
            >
              🇬🇧 English
            </MenuItem>
          </Menu>

          <Divider />

          {/* Shop */}
          <ListItem button onClick={() => handleNavigate('/shop')}>
            <ListItemIcon>
              <ShoppingBag />
            </ListItemIcon>
            <ListItemText primary={t('common.shop')} />
          </ListItem>

          {/* Track Order */}
          <ListItem button onClick={() => handleNavigate('/order-tracking')}>
            <ListItemIcon>
              <LocalShipping />
            </ListItemIcon>
            <ListItemText primary={t('common.trackOrder')} />
          </ListItem>

          {isAuthenticated ? (
            <>
              <Divider />
              {/* Profile */}
              <ListItem button onClick={() => handleNavigate('/profile')}>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText primary={t('common.myProfile')} />
              </ListItem>

              {/* Orders */}
              <ListItem button onClick={() => handleNavigate('/orders')}>
                <ListItemIcon>
                  <ShoppingBag />
                </ListItemIcon>
                <ListItemText primary={t('common.myOrders')} />
              </ListItem>

              {/* Admin Panel */}
              {user?.role === 'ADMIN' && (
                <ListItem button onClick={() => handleNavigate('/admin')}>
                  <ListItemIcon>
                    <AdminPanelSettings />
                  </ListItemIcon>
                  <ListItemText primary={t('common.adminPanel')} />
                </ListItem>
              )}

              <Divider />

              {/* Logout */}
              <ListItem button onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary={t('common.logout')} />
              </ListItem>
            </>
          ) : (
            <>
              <Divider />
              {/* Login */}
              <ListItem button onClick={() => handleNavigate('/login')}>
                <ListItemIcon>
                  <Login />
                </ListItemIcon>
                <ListItemText primary={t('common.login')} />
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Drawer>
    </>
  );
};

export default Header;